/**
 * How many characters of the message are kept in front of the term.
 *
 * Measured, not chosen: the inbox row's preview line is 209px, which is about
 * 38 characters at the 12px it is set in. A two word query like 'crewman
 * position' is 16 of them, and the leading ellipsis costs 2, so a 12 character
 * lead puts the end of the term at character 30 at the latest and leaves a few
 * for the tail. Larger and the term itself starts being cut by the ellipsis,
 * which is the whole failure this function exists to prevent.
 *
 * A ceiling rather than a target: the cut moves forward to a word boundary, so
 * the real lead is this or less. That headroom is what absorbs the one state
 * where the line is not 209px: a row in selecting mode gives 28px to the
 * checkbox column (`[data-selecting] .spacer` in ChannelPreview.module.css),
 * leaving about 33 characters, which a term ending by 30 still fits inside.
 * Change the row's font size or that column and this wants re-measuring.
 */
const LEAD = 12;

/** Marks the front of a snippet that starts partway through its message. */
const ELLIPSIS = '… ';

/**
 * The part of a message worth showing as a search result: the term, with just
 * enough in front of it to read as a sentence.
 *
 * A message shown from its head is the bug. At 38 characters a match anywhere
 * past the opening clause falls off the right edge, so the row carries the
 * employer's own words nowhere on it and reads as a result the app cannot
 * explain — which is exactly what a filtered list looked like before this.
 *
 * Only the head is cut here. The tail is left to the stylesheet's existing
 * `text-overflow: ellipsis`, because CSS can already do that and doing it twice
 * would mean guessing at a width the browser knows exactly.
 *
 * The guarantee is that the *start* of the term is visible, never that all of
 * it is: a query longer than the line gets cut like any other text, and cutting
 * it is more honest than shrinking it or wrapping to a second line.
 */
export function windowAround(text: string, term: string): string {
  const needle = term.trim().toLowerCase();
  if (needle === '') return text;

  const at = text.toLowerCase().indexOf(needle);
  // Not in this text, or already near enough to the front to read normally.
  if (at === -1 || at <= LEAD) return text;

  const target = at - LEAD;
  // Forward to the next word boundary, not back to the previous one. Backing up
  // opens on a whole word too, but a long word before the term ('interested')
  // pushes the term further right than the lead allows — measured at 20
  // characters against a 12 character lead, which spends a third of a 38
  // character line before the employer's own words start. Going forward can
  // only ever shorten the lead, so the term's position is bounded.
  const space = text.indexOf(' ', target);
  // No space before the term means the run in front of it is one unbroken token
  // (a url, a file name). Open on the term itself rather than skipping past it.
  const cut = space === -1 || space >= at ? at : space + 1;
  return ELLIPSIS + text.slice(cut).trimStart();
}
